import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  useESGCollaborationStore,
  useESGDisclosureSelectionStore,
  useESGReportWorkflowStore,
} from "@/features/esg/hooks/useESGReportStore";
import { useTran } from "@/hooks";
import { PATHS } from "@/lib/router";
import { ChevronDown, ChevronRight, ExternalLink } from "lucide-react";
import { observer } from "mobx-react-lite";

const DisclosureSelectionCard = observer(({ category }) => {
  const { t } = useTran();
  const navigate = useNavigate();
  const {
    selectedCodes,
    selectByCategory,
    unselectByCategory,
    toggleDisclosure,
    groupedByCategory,
  } = useESGDisclosureSelectionStore();
  const { expandedDisclosures, toggleExpandDisclosure } =
    useESGReportWorkflowStore();
  const { delegatedDisclosures } = useESGCollaborationStore();

  const disclosures = groupedByCategory[category] || [];

  const isSelectedAll =
    disclosures.length > 0 &&
    disclosures.every((d) => selectedCodes.includes(d.code));

  const groupedDisclosures = useMemo(() => {
    const map = new Map();

    for (const d of disclosures) {
      if (d.isMasterMetricGroup) {
        map.set(d.code, {
          parentCode: d.code,
          parentTitle: d.title,
          isSingle: false,
          isMasterMetricGroup: true,
          children: d.children || [],
        });
        continue;
      }
      const isCustom = d.isCustom;
      const isSingle = !isCustom && !d.parentCode;
      const key = isCustom ? "custom" : d.parentCode || d.code;
      if (!map.has(key)) {
        map.set(key, {
          parentCode: isCustom ? "Custom Disclosures" : d.parentCode || d.code,
          parentTitle: isCustom
            ? "User created disclosures"
            : d.parentTitle || d.title,
          isSingle,
          children: [],
        });
      }
      const group = map.get(key);
      if (!group.children.some((child) => child.code === d.code)) {
        group.children.push(d);
      }
    }
    return Array.from(map.values());
  }, [disclosures]);

  return (
    <div className="flex flex-col min-h-0 h-full p-3 bg-card border-border border rounded-radius space-y-3 overflow-hidden">
      <div className="flex items-center justify-between">
        <p className="text-sm font-semibold">{t(category)}</p>
        <Button
          variant="ghost"
          className="text-primary text-xs"
          onClick={() =>
            isSelectedAll
              ? unselectByCategory(category)
              : selectByCategory(category)
          }
          size="sm"
        >
          {t(isSelectedAll ? "Unselect All" : "Select All")}
        </Button>
      </div>

      <div className="flex-1 min-h-0 space-y-2 overflow-y-auto pr-1">
        {groupedDisclosures.map((group) => {
          const isSingle = group.isSingle;
          const singleCode = isSingle ? group.children[0].code : null;
          const hasSelectedChild =
            group.children.length > 0 &&
            group.children.some((c) => selectedCodes.includes(c.code));
          const isExpanded =
            !isSingle &&
            (group.isMasterMetricGroup
              ? selectedCodes.includes(group.parentCode) ||
                expandedDisclosures.has(group.parentCode)
              : hasSelectedChild || expandedDisclosures.has(group.parentCode));
          const contentId = `parent-${group.parentCode}`;

          return (
            <div
              className="rounded-md border-border border"
              key={group.parentCode}
            >
              {isSingle ? (
                <label className="p-2 flex items-center gap-x-2 cursor-pointer hover:bg-muted border-border w-full">
                  <Checkbox
                    id={singleCode}
                    checked={selectedCodes.includes(singleCode)}
                    onCheckedChange={() => toggleDisclosure(singleCode)}
                  />
                  <div>
                    <p className="font-semibold text-sm text-foreground">
                      {group.parentCode}
                    </p>
                    <p className="text-muted-foreground text-xs">
                      {group.parentTitle}
                    </p>
                  </div>
                </label>
              ) : group.isMasterMetricGroup ? (
                <button
                  type="button"
                  className={`p-2 flex items-center justify-between cursor-pointer hover:bg-muted ${isExpanded ? "border-b" : ""} border-border w-full text-left`}
                  aria-expanded={isExpanded}
                  aria-controls={contentId}
                  onClick={() => toggleExpandDisclosure(group.parentCode)}
                >
                  <div className="flex items-center gap-x-2">
                    <Checkbox
                      id={group.parentCode}
                      checked={selectedCodes.includes(group.parentCode)}
                      onCheckedChange={() => toggleDisclosure(group.parentCode)}
                      onClick={(event) => event.stopPropagation()}
                    />
                    <div>
                      <p className="font-semibold text-sm text-foreground">
                        {group.parentCode}
                      </p>
                      <p className="text-muted-foreground text-xs">
                        {group.parentTitle}
                      </p>
                    </div>
                  </div>
                  {isExpanded ? (
                    <ChevronDown size={16} className="text-foreground" />
                  ) : (
                    <ChevronRight size={16} className="text-foreground" />
                  )}
                </button>
              ) : (
                <button
                  type="button"
                  className={`p-2 flex items-center justify-between cursor-pointer hover:bg-muted ${isExpanded ? "border-b" : ""} border-border w-full text-left`}
                  aria-expanded={isExpanded}
                  aria-controls={contentId}
                  onClick={() => toggleExpandDisclosure(group.parentCode)}
                >
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-semibold text-sm text-foreground">
                          {group.parentCode}
                        </p>
                        <p className="text-muted-foreground text-xs">
                          {group.parentTitle}
                        </p>
                      </div>
                      {group.parentCode === "Custom Disclosures" && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-primary text-xs h-auto py-1 px-2 hover:bg-primary/10"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(PATHS.ESG_CUSTOM_DISCLOSURES);
                          }}
                        >
                          <ExternalLink size={12} className="mr-1" />
                          {t("Manage Library")}
                        </Button>
                      )}
                    </div>
                  </div>
                  {isExpanded ? (
                    <ChevronDown size={16} className="text-foreground ml-2" />
                  ) : (
                    <ChevronRight size={16} className="text-foreground ml-2" />
                  )}
                </button>
              )}

              {!isSingle && isExpanded && (
                <div id={contentId} className="p-2 space-y-1">
                  {group.isMasterMetricGroup
                    ? group.children.map((d) => (
                        <div
                          key={d.code}
                          className="flex text-xs items-center gap-x-2 p-1"
                        >
                          <span className="font-semibold text-[10px] bg-primary/10 text-primary px-1.5 py-0.5 rounded">
                            {d.framework}
                          </span>
                          <p className="font-semibold">{d.code}</p>
                          <p className="text-muted-foreground">{d.title}</p>
                        </div>
                      ))
                    : group.children.map((d) => {
                        const delegated = delegatedDisclosures.get(d.code);
                        const isChecked =
                          Boolean(delegated) || selectedCodes.includes(d.code);
                        const handleClick = () =>
                          delegated ? null : toggleDisclosure(d.code);
                        return (
                          <div
                            key={d.code}
                            role="button"
                            className={`flex text-xs items-center gap-x-2 hover:bg-muted rounded-md p-1 ${delegated ? "opacity-70 cursor-not-allowed" : "cursor-pointer"}`}
                            onClick={handleClick}
                          >
                            <Checkbox
                              id={d.code}
                              checked={isChecked}
                              onCheckedChange={handleClick}
                              onClick={(event) => event.stopPropagation()}
                            />
                            <p className="font-semibold">{d.code}</p>
                            <p className="text-muted-foreground">{d.title}</p>
                            {delegated && (
                              <p className="text-orange-900 dark:text-yellow-500">
                                (Delegated: {delegated.email})
                              </p>
                            )}
                          </div>
                        );
                      })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
});

export default DisclosureSelectionCard;
